import { Button, Input, RadioButtonGroup } from '@fullscreendigital/carrefour-dex-fe'
import { Grid } from '@mui/material'
import { Modal } from 'components/core-ui/Modal'
import { useEffect, useMemo, useState } from 'react'
import { getInitialValues, getValidationSchema } from './helpers'
import { useFormik } from 'formik'
import { useMutation } from 'react-query'
import { useNotification } from 'hooks'
import { toLocationPayload } from './serializers'
import { makeCorePostRequest, makeCorePutRequest } from 'utils/api'
import { handleQueryError } from 'utils/helpers'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import {
  getLegalEntititesOptions,
  getLocation,
  getLocationRegionsOptions,
  getLocationStatusesOptions,
  getLocationSubtypesOptions,
  getLocationSystems,
  getLocationTypesOptions
} from 'components/location/api'
import ls from './LocationModal.modules.scss'
import { SimpleItem } from 'components/location/interfaces'
import { NewAddress } from './NewAddress'
import { ExistingAddress } from './ExistingAddress'

interface LocationModalProps {
  isModalOpen: boolean
  closeModal: Function
  shouldRefreshData: boolean
  setShouldRefreshData: Function
  isEdit?: boolean
  editItemId?: number
}

const LocationModal = (props: LocationModalProps) => {
  const { isModalOpen, closeModal, isEdit, editItemId, shouldRefreshData, setShouldRefreshData } =
    props

  const [selectedLocationTypeId, setSelectedLocationTypeId] = useState<number | null>(null)
  const [isNewAddress, setIsNewAddress] = useState<string>('true')

  const { showNotification } = useNotification()

  const { data: editItem } = getLocation(showNotification, editItemId, shouldRefreshData)

  const { data: legalEntitiesOptions } = getLegalEntititesOptions(showNotification)
  const { data: locationStatusesOptions } = getLocationStatusesOptions(showNotification)
  const { data: locationTypesOptions } = getLocationTypesOptions(showNotification)
  const { data: locationSubtypesOptions } = getLocationSubtypesOptions(
    showNotification,
    selectedLocationTypeId
  )
  const { data: locationRegionsOption } = getLocationRegionsOptions(showNotification)
  const { data: locationSystems } = getLocationSystems(showNotification)

  useEffect(() => {
    if (isEdit && editItem) {
      setSelectedLocationTypeId(editItem.subtype.type.id)
    }
  }, [editItem, isEdit])

  const initialValues = useMemo(
    () => getInitialValues(editItem, locationSystems),
    [editItem, locationSystems]
  )

  const validationSchema = useMemo(
    () => getValidationSchema(isNewAddress),
    [editItem, isNewAddress]
  )

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      mutate(toLocationPayload(values, isNewAddress))
    }
  })

  const mutateLocation = async values => {
    editItem
      ? await makeCorePutRequest(`/location-management/location-usage/${editItem.id}`, values)
      : await makeCorePostRequest(`/location-management/location-usage`, values)
  }

  const { isLoading, mutate } = useMutation(mutateLocation, {
    onSuccess: res => {
      setShouldRefreshData(true)
      closeModal()
      formik.resetForm()
      showNotification({
        message: `The location was successfully ${editItem.id ? 'edited' : 'created'}.`,
        type: 'success'
      })
    },
    onError: error =>
      handleQueryError(
        error,
        `Error for ${editItem.id ? 'editing location.' : 'adding location.'}`,
        showNotification
      )
  })

  const cancelModal = () => {
    closeModal()
    formik.resetForm()
  }

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={cancelModal}
      modalTitle={isEdit ? 'Edit Location' : 'New Location'}
      modalWidth="50%"
    >
      <Grid container spacing={4}>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="locationUsageName"
            label="Location name"
            fullWidth
            variant="outlined"
            value={formik.values.locationUsageName}
            onChange={event => formik.setFieldValue('locationUsageName', event.currentTarget.value)}
            error={!!formik.errors.locationUsageName}
            helperText={formik.errors.locationUsageName as string}
            required
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="locationUsageCode"
            label="Location code"
            fullWidth
            variant="outlined"
            value={formik.values.locationUsageCode}
            onChange={event => formik.setFieldValue('locationUsageCode', event.currentTarget.value)}
            error={!!formik.errors.locationUsageCode}
            helperText={formik.errors.locationUsageCode as string}
            required
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <AsyncAutocomplete
            variant="outlined"
            name="legalEntity"
            label="Legal entity"
            value={formik.values.legalEntity}
            options={legalEntitiesOptions || []}
            onChange={value => formik.setFieldValue('legalEntity', value)}
            error={!!formik.errors.legalEntity}
            helperText={formik.errors.legalEntity as string}
            required
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <AsyncAutocomplete
            variant="outlined"
            name="locationStatus"
            label="Location status"
            value={formik.values.locationStatus}
            options={locationStatusesOptions || []}
            onChange={value => formik.setFieldValue('locationStatus', value)}
            error={!!formik.errors.locationStatus}
            helperText={formik.errors.locationStatus as string}
            required
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <AsyncAutocomplete
            variant="outlined"
            name="locationType"
            label="Type"
            value={formik.values.locationType}
            options={locationTypesOptions || []}
            onChange={value => {
              setSelectedLocationTypeId(value?.value)
              formik.setFieldValue('locationSubtype', null)
              formik.setFieldValue('locationType', value)
            }}
            error={!!formik.errors.locationType}
            helperText={formik.errors.locationType as string}
            required
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <AsyncAutocomplete
            variant="outlined"
            name="locationSubtype"
            label="Subtype"
            value={formik.values.locationSubtype}
            options={locationSubtypesOptions || []}
            onChange={value => formik.setFieldValue('locationSubtype', value)}
            error={!!formik.errors.locationSubtype}
            helperText={formik.errors.locationSubtype as string}
            required
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <AsyncAutocomplete
            variant="outlined"
            name="locationRegion"
            label="Regions"
            value={formik.values.locationRegions}
            options={locationRegionsOption || []}
            onChange={value => formik.setFieldValue('locationRegions', value)}
            groupBy={option => option.groupBy}
            multiple
            error={!!formik.errors.locationRegions}
            helperText={formik.errors.locationRegions as string}
          />
        </Grid>
        <Grid item xs={12} className={ls.sectionTitle}>
          External ids
        </Grid>
        {locationSystems?.map((system: SimpleItem) => {
          const formikSystem = formik.values.externalIdList.find(
            locSystem => locSystem.id === system.id
          )
          return (
            <Grid xs={12} md={6} item key={system.id}>
              <Input
                type="text"
                name="externalIdList"
                label={system.name}
                fullWidth
                variant="outlined"
                value={formikSystem?.value}
                onChange={value => {
                  const externalIdList = formik.values.externalIdList.map(externalId =>
                    externalId.id === system.id
                      ? { ...externalId, value: value.currentTarget.value }
                      : externalId
                  )
                  formik.setFieldValue('externalIdList', externalIdList)
                }}
              />
            </Grid>
          )
        })}
        <Grid item xs={12} className={ls.sectionTitle}>
          Address
        </Grid>
        <Grid item xs={12}>
          <RadioButtonGroup
            label=""
            dotStyle
            name="isNewAddress"
            onChange={(e, option) => setIsNewAddress(option === 'true' ? 'true' : 'false')}
            value={isNewAddress === 'true' ? 'true' : 'false'}
            row
            options={[
              {
                label: 'New address',
                value: 'true'
              },
              {
                label: 'Use same address as another location',
                value: 'false'
              }
            ]}
          />
        </Grid>

        {isNewAddress === 'true' ? (
          <NewAddress formik={formik} />
        ) : (
          <ExistingAddress formik={formik} />
        )}

        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Cancel" onClick={cancelModal} />
          <Button
            variant="contained"
            type="submit"
            label="Submit"
            onClick={() => formik.handleSubmit()}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default LocationModal
